/** Mount configuration: where this app sits in the page's URL space and how
 *  it reaches its API. The defaults are the standalone app (mounted at the
 *  site root, API under /api/v1, the session from api/client.ts's own stored
 *  tokens), so nothing changes unless configureApp() is called.
 *
 *  A host page that mounts these routes under a base path inside its own
 *  router calls configureApp() once, before the first render. Two rules keep
 *  the app working there:
 *  - Every in-app link or redirect target (Link/Navigate `to`, navigate(),
 *    an `<a href>`) goes through appPath('/map'). Path builders such as
 *    attentionCards.ts or discrepancyLink.ts return app paths; the component
 *    that hands one to the router wraps it.
 *  - Every pathname read compares stripAppBase(location.pathname).
 *  Route definitions inside <Routes> stay as they are: as descendant routes
 *  of the host's `<base>/*` route they match relative to it.
 *  API URLs are not app paths: they start with apiBase() (api/client.ts). */

export interface AppConfig {
  /** URL path the app is mounted under, e.g. '/tools'. '' is the site root. */
  basePath: string
  /** Prefix of every API URL. */
  apiBase: string
  /** The host's session token, read on every request. When set, the client
   *  sends it instead of its own stored token and never runs its own refresh
   *  flow. null: the stored token. */
  getToken: (() => string | null) | null
  /** Called on a 401 instead of the client's own handling (refresh, clearing
   *  the stored tokens, AuthProvider's handler); the request still rejects.
   *  null: the client's own handling. */
  onUnauthorized: (() => void) | null
}

const DEFAULTS: AppConfig = {
  basePath: '',
  apiBase: '/api/v1',
  getToken: null,
  onUnauthorized: null,
}

let config: AppConfig = DEFAULTS

/** Set the mount configuration. Each call replaces the whole configuration
 *  (unset options take their defaults), so configureApp({}) restores the
 *  standalone app. */
export function configureApp(options: Partial<AppConfig>): void {
  const basePath = (options.basePath ?? DEFAULTS.basePath).replace(/\/+$/, '')
  if (basePath && !/^\/[^?#]*$/.test(basePath)) {
    throw new Error(
      `configureApp: basePath must be a path starting with '/', got ${JSON.stringify(options.basePath)}`,
    )
  }
  config = {
    basePath,
    apiBase: (options.apiBase ?? DEFAULTS.apiBase).replace(/\/+$/, ''),
    getToken: options.getToken ?? null,
    onUnauthorized: options.onUnauthorized ?? null,
  }
}

/** The current mount configuration. */
export function getAppConfig(): Readonly<AppConfig> {
  return config
}

/** Prefix of every API URL ('/api/v1' by default). */
export function apiBase(): string {
  return config.apiBase
}

/** The router URL for an in-app path such as '/map?feature=Meter:9'. Identity
 *  at the site root; under a base path, '/' becomes the base itself. Anything
 *  that is not an absolute path comes back unchanged. */
export function appPath(path: string): string {
  const base = config.basePath
  if (!base || !path.startsWith('/')) return path
  return path === '/' || path.startsWith('/?') || path.startsWith('/#')
    ? base + path.slice(1)
    : base + path
}

/** The in-app path for a router pathname: the inverse of appPath, for
 *  comparing location.pathname with '/outages'. Like the router, the base
 *  matches case-insensitively and only at a segment boundary; a pathname
 *  outside the base comes back unchanged. */
export function stripAppBase(pathname: string): string {
  const base = config.basePath
  if (!base) return pathname
  if (!pathname.toLowerCase().startsWith(base.toLowerCase())) return pathname
  const rest = pathname.slice(base.length)
  if (rest && !rest.startsWith('/')) return pathname
  return rest || '/'
}
